import { useCallback, useEffect, useState } from 'react'
import { FetchStatus } from '../../../../types/fetch-status.types'
import constants from '../../../../constants'
import { MedicalReimbursement } from '../../../../database/tables'
import { GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid'
import InfoIcon from '@mui/icons-material/Info'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import IconEdit from '@mui/icons-material/Edit'
import MedicalReimbursementService from '../../../../services/medical-reimbursement'
import { ReimbursementType } from '../../../../database/enums/ReimbursementType.enum'
import ReimbursementTypeEnumHelper from '../../../../database/enums/helpers/reimbursement-type.helper'
import CurrencyEnumHelper from '../../../../database/enums/helpers/currency.helper'
import { useNavigate } from 'react-router'
import links from '../../../../routes/links'
import adminMedicalReimbursementApprovalPopupStore from '../../../../store/adminMedicalReimbursementApprovalPopupStore'
import { ApprovalStatus } from '../../../../database/enums/ApprovalStatus.enum'
import authStore from '../../../../store/authStore'
import PERMISSIONS from '../../../../private/permissions'
import toast from 'react-hot-toast'

const { defaultActionWidth, defaultBaseWidth, defaultDateTimeWidth, defaultDateWidth } = constants.dataGrid
export const scopeNamePrefix = 'admin-medical-reimbursements-unresponded'

export default function useAdminMedicalReimbursementsUnrespondedPageViewModel() {
  const navigate = useNavigate()
  const user = authStore(state => state.user)

  const [dataFetchStatus, setDataFetchStatus] = useState<FetchStatus>(constants.fetchStatus.default)
  const [medicalReimbursements, setMedicalReimbursements] = useState<MedicalReimbursement[]>([])

  const canApproval = useCallback(() => {
    if (!!user && !!user.email) return PERMISSIONS.getApprovalEmails().includes(user.email)
    return false
  }, [user])()

  const tableColumns: GridColDef<MedicalReimbursement>[] = [
    { field: 'name', headerName: 'Name', editable: true, minWidth: defaultBaseWidth, type: 'string' },
    { field: 'serviceDate', headerName: 'Service Date', editable: true, minWidth: defaultDateWidth, type: 'date' },
    {
      field: 'reimbursementType',
      headerName: 'Type',
      minWidth: defaultBaseWidth,
      type: 'singleSelect',
      valueOptions: Object.values(ReimbursementType),
      getOptionLabel: value => ReimbursementTypeEnumHelper.getLabel(value as ReimbursementType),
    },
    { field: 'vendor', headerName: 'Vendor', editable: true, minWidth: defaultBaseWidth, type: 'string' },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      editable: true,
      minWidth: defaultBaseWidth,
      type: 'string',
    },
    {
      field: 'totalAmountClaimed',
      headerName: 'Total Amount Claimed',
      flex: 1,
      minWidth: defaultBaseWidth,
      type: 'string',
      valueGetter: (value: MedicalReimbursement['totalAmountClaimed']) =>
        `${CurrencyEnumHelper.getSymbol(value.currency)} ${Intl.NumberFormat().format(value.value)}`,
    },
    { field: 'createdAt', headerName: 'Created At', editable: true, minWidth: defaultDateTimeWidth, type: 'dateTime' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: defaultActionWidth,
      type: 'actions',
      getActions: (params: GridRowParams<MedicalReimbursement>) => {
        const isResponded = params.row.approvalStatus !== ApprovalStatus.UNRESPONDED
        return [
          <GridActionsCellItem
            key={`${scopeNamePrefix}-table-actions-${params.id}-info`}
            icon={<InfoIcon color='info' />}
            label='Info'
            onClick={() => navigate(links.admin.medicalReimbursementDetail({ id: params.row.id }))}
            showInMenu
          />,
          <GridActionsCellItem
            key={`${scopeNamePrefix}-table-actions-${params.id}-edit`}
            icon={<IconEdit color='warning' />}
            label='Edit'
            onClick={() => onEditClick(isResponded, params.row)}
            showInMenu
          />,
          <GridActionsCellItem
            key={`${scopeNamePrefix}-table-actions-${params.id}-approve`}
            icon={<CheckCircleIcon color={canApproval ? 'success' : 'disabled'} />}
            label='Approve'
            onClick={() => onApproveClick(params.row)}
          />,
          <GridActionsCellItem
            key={`${scopeNamePrefix}-table-actions-${params.id}-reject`}
            icon={<CancelIcon color={canApproval ? 'error' : 'disabled'} />}
            label='Reject'
            onClick={() => onRejectClick(params.row)}
          />,
        ]
      },
    },
  ]

  async function fetchMedicalReimbursementsUnrespondedData() {
    setDataFetchStatus({ ...constants.fetchStatus.default, isLoading: true })
    await MedicalReimbursementService.getUnresponded()
      .then(res => {
        setMedicalReimbursements(res)
        setDataFetchStatus({ ...constants.fetchStatus.default, isSuccess: true })
      })
      .catch(error => {
        console.log(error)
        setDataFetchStatus({ ...constants.fetchStatus.default, isError: true })
      })
  }

  useEffect(() => {
    fetchMedicalReimbursementsUnrespondedData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function onEditClick(isResponded: boolean, row: MedicalReimbursement) {
    // TODO: remove comment if edit restriction is needed
    // if (isResponded) toast.error('This document is not editable anymore!')
    // else
    navigate(links.admin.medicalReimbursementEdit({ id: row.id }))
  }

  function onApproveClick(data: MedicalReimbursement) {
    if (canApproval)
      adminMedicalReimbursementApprovalPopupStore.setState({
        approvalStatus: ApprovalStatus.APPROVED,
        data,
        open: true,
      })
    else toast.error("You don't have permission to do this action!")
  }

  function onRejectClick(data: MedicalReimbursement) {
    if (canApproval)
      adminMedicalReimbursementApprovalPopupStore.setState({
        approvalStatus: ApprovalStatus.REJECTED,
        data,
        open: true,
      })
    else toast.error("You don't have permission to do this action!")
  }

  function onApprovalPopupConfirm() {
    fetchMedicalReimbursementsUnrespondedData()
  }

  return {
    dataFetchStatus,
    fetchMedicalReimbursementsUnrespondedData,
    tableData: {
      columns: tableColumns,
      rows: medicalReimbursements,
    },
    onApproveClick,
    onRejectClick,
    onApprovalPopupConfirm,
  }
}
